import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Field, FieldGroup, Input, Select } from '@itds/elements/index.js';
import {
  GUARANTEED_UNIVERSAL_LIFE_PRODUCTS,
  HEALTH_CATEGORIES,
  OTHER_PRODUCTS,
  ROP_PRODUCTS,
  TERM_LIFE_INSURANCE_PRODUCTS
} from '@itds/Constants';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';

const getRateLimitValues = data => ({
  rate_limit_replenishment_period_seconds:
    'rate_limit_replenishment_period_seconds' in data ? data.rate_limit_replenishment_period_seconds : '',
  rate_limit_token_limit: 'rate_limit_token_limit' in data ? data.rate_limit_token_limit : '',
  rate_limit_tokens_per_period: 'rate_limit_tokens_per_period' in data ? data.rate_limit_tokens_per_period : ''
});

const getMonthlyUsageValues = data => ({
  monthly_request_limit: 'monthly_request_limit' in data ? data.monthly_request_limit : ''
});

const General = ({ website_quoter, updateWebsiteQuoter }) => {
  const { member_names } = useContext(AgencyContext);
  const [errors, setErrors] = useState([]);
  const [data, setData] = useState({
    name: website_quoter.name,
    member_id: website_quoter.member_id,
    is_beta: !!website_quoter.is_beta,
    is_live: !!website_quoter.is_live,
    is_archived: website_quoter.is_archived,
    default_face_amount: website_quoter.default_face_amount,
    default_health_category: website_quoter.default_health_category,
    default_product_type: website_quoter.default_product_type,
    hide_config_for_assigned_member: website_quoter.hide_config_for_assigned_member,
    smart_office_office_name: website_quoter.smart_office_office_name,
    smart_office_username: website_quoter.smart_office_username,
    smart_office_site_name: website_quoter.smart_office_site_name,
    smart_office_promo_code: website_quoter.smart_office_promo_code,
    lead_form_btn_label: website_quoter.lead_form_btn_label,
    lead_form_success_message: website_quoter.lead_form_success_message,
    google_recaptcha_site_key: website_quoter.google_recaptcha_site_key,
    google_recaptcha_secret_key: website_quoter.google_recaptcha_secret_key,
    show_results_in_modal: website_quoter.show_results_in_modal,
    is_deactivated: website_quoter.is_deactivated,
    crm: website_quoter.crm || 'ixn',
    insureio_api_key: website_quoter.insureio_api_key,
    insureio_subdomain: website_quoter.insureio_subdomain,
    insureio_brand_id: website_quoter.insureio_brand_id,
    insureio_agent_id: website_quoter.insureio_agent_id,
    insureio_lead_type: website_quoter.insureio_lead_type,
    rate_limit_enabled: website_quoter.rate_limit_enabled || false,
    ...getRateLimitValues(website_quoter),
    monthly_limit_enabled: website_quoter.monthly_limit_enabled || false,
    ...getMonthlyUsageValues(website_quoter)
  });

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleCheckboxChange = (name, e) => {
    const checked = e.target.checked;

    if (name === 'rate_limit_enabled' && !checked) {
      setData({
        ...data,
        rate_limit_enabled: checked,
        ...getRateLimitValues(website_quoter)
      });

      return;
    }

    if (name === 'monthly_limit_enabled' && !checked) {
      setData({
        ...data,
        monthly_limit_enabled: checked,
        ...getMonthlyUsageValues(website_quoter)
      });

      return;
    }

    setData({ ...data, [name]: checked });
  };

  const handleDataSourceChange = () => {
    setData({
      ...data,
      is_live: !data.is_live,
      is_beta: !data.is_beta
    });
  };

  const handleArchiveToggle = () => {
    setData({ ...data, is_archived: !data.is_archived });
    updateWebsiteQuoter({ is_archived: !data.is_archived });
  };

  const rateLimitsReadOnly = !data.rate_limit_enabled || data.is_deactivated || data.is_archived;
  const monthlyUsageReadOnly =
    !data.monthly_limit_enabled || data.is_deactivated || data.is_archived;
  const sectionHeaderStyle = {
    color: '#666',
    fontSize: 13,
    fontWeight: 600,
    margin: '0 0 12px'
  };

  const handleSave = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }

    if (data.crm === 'insureio') {
      const required_fields = [
        'insureio_api_key',
        'insureio_subdomain',
        'insureio_brand_id',
        'insureio_agent_id'
      ];

      required_fields.forEach(field => {
        if (!data[field]) {
          errors.push(field);
        }
      });
    }

    if (data.crm === 'smart_office') {
      const required_fields = [
        'smart_office_office_name',
        'smart_office_username',
        'smart_office_site_name'
      ];

      required_fields.forEach(field => {
        if (!data[field]) {
          errors.push(field);
        }
      });
    }

    setErrors(errors);

    if (!errors.length) {
      updateWebsiteQuoter(data);
    }
  };

  return (
    <div>
      <Field>
        <Checkbox
          checked={data.is_deactivated}
          disabled={data.is_archived}
          label='Deactivated'
          onChange={handleCheckboxChange.bind(null, 'is_deactivated')}
        />
      </Field>

      <Field label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleInputChange.bind(null, 'name')}
          readOnly={data.is_archived}
          required={true}
          value={data.name}
        />
      </Field>

      <Field label='Member'>
        <Select
          onChange={handleInputChange.bind(null, 'member_id')}
          options={[{ value: '', label: 'Unassigned' }].concat(member_names)}
          readOnly={data.is_archived}
          value={data.member_id}
        />
      </Field>

      <Field label='Data Source'>
        <Select
          onChange={handleDataSourceChange}
          options={[
            { value: 'is_live', label: 'Live' },
            { value: 'is_beta', label: 'Beta' }
          ]}
          readOnly={data.is_archived}
          value={data.is_live ? 'is_live' : 'is_beta'}
        />
      </Field>

      <Field label='Default Face Amount'>
        <Input
          format='currency'
          onChange={handleInputChange.bind(null, 'default_face_amount')}
          readOnly={data.is_archived}
          value={data.default_face_amount}
        />
      </Field>

      <Field label='Default Health Category'>
        <Select
          onChange={handleInputChange.bind(null, 'default_health_category')}
          options={HEALTH_CATEGORIES.map(category => {
            return {
              value: category
            };
          })}
          readOnly={data.is_archived}
          value={data.default_health_category}
        />
      </Field>

      <Field label='Default Product Type'>
        <Select
          onChange={handleInputChange.bind(null, 'default_product_type')}
          options={[
            ...GUARANTEED_UNIVERSAL_LIFE_PRODUCTS,
            ...OTHER_PRODUCTS,
            ...ROP_PRODUCTS,
            ...TERM_LIFE_INSURANCE_PRODUCTS
          ].map(category => {
            return {
              value: category
            };
          })}
          readOnly={data.is_archived}
          value={data.default_product_type}
        />
      </Field>

      <Field>
        <Checkbox
          checked={data.hide_config_for_assigned_member}
          disabled={data.is_archived}
          label='Hide configuration for assigned member'
          onChange={handleCheckboxChange.bind(null, 'hide_config_for_assigned_member')}
        />
      </Field>

      <Field>
        <Checkbox
          checked={data.show_results_in_modal}
          disabled={data.is_archived}
          label='Show results in modal'
          onChange={handleCheckboxChange.bind(null, 'show_results_in_modal')}
        />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Field label='Google reCAPTCHA Site Key (v2 Checkbox)' style={{ width: '49%' }}>
          <Input
            onChange={handleInputChange.bind(null, 'google_recaptcha_site_key')}
            readOnly={data.is_archived}
            value={data.google_recaptcha_site_key}
          />
        </Field>
        <Field label='Google reCAPTCHA Secret Key (v2 Checkbox)' style={{ width: '49%' }}>
          <Input
            onChange={handleInputChange.bind(null, 'google_recaptcha_secret_key')}
            readOnly={data.is_archived}
            value={data.google_recaptcha_secret_key}
          />
        </Field>
      </div>

      <Field label='Lead Flow CRM'>
        <Select
          onChange={handleInputChange.bind(null, 'crm')}
          options={[
            { value: 'ixn', label: 'IXN' },
            { value: 'insureio', label: 'Insureio' },
            { value: 'smart_office', label: 'Smart Office' },
            { value: 'custom', label: 'Custom' }
          ]}
          readOnly={data.is_archived}
          value={data.crm}
        />
      </Field>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Rate Limits</div>
        <Field label=''>
          <Checkbox
            checked={data.rate_limit_enabled}
            disabled={data.is_archived || data.is_deactivated}
            label='Enabled'
            onChange={handleCheckboxChange.bind(null, 'rate_limit_enabled')}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: '49%' }}>
            <Field label='Token Limit'>
              <Input
                onChange={handleInputChange.bind(null, 'rate_limit_token_limit')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_token_limit || ''}
              />
            </Field>
          </div>
          <div style={{ width: '49%' }}>
            <Field label='Tokens Per Period'>
              <Input
                onChange={handleInputChange.bind(null, 'rate_limit_tokens_per_period')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_tokens_per_period || ''}
              />
            </Field>
          </div>
        </div>

        <div style={{ width: '49%' }}>
          <Field label='Replenishment Period Seconds'>
            <Input
              onChange={handleInputChange.bind(null, 'rate_limit_replenishment_period_seconds')}
              readOnly={rateLimitsReadOnly}
              type='number'
              value={data.rate_limit_replenishment_period_seconds || ''}
            />
          </Field>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Monthly Usage</div>
        <Field label=''>
          <Checkbox
            checked={data.monthly_limit_enabled}
            disabled={data.is_archived || data.is_deactivated}
            label='Enabled'
            onChange={handleCheckboxChange.bind(null, 'monthly_limit_enabled')}
          />
        </Field>

        <div style={{ width: '49%' }}>
          <Field label='Monthly Request Limit'>
            <Input
              onChange={handleInputChange.bind(null, 'monthly_request_limit')}
              readOnly={monthlyUsageReadOnly}
              type='number'
              value={data.monthly_request_limit || ''}
            />
          </Field>
        </div>
      </div>

      {data.crm === 'insureio' ? (
        <FieldGroup contained={true} label='Insureio Settings'>
          <Field label='API Key'>
            <Input
              invalid={errors.includes('insureio_api_key')}
              onChange={handleInputChange.bind(null, 'insureio_api_key')}
              readOnly={data.is_archived}
              required={true}
              value={data.insureio_api_key}
            />
          </Field>
          <Field label='Subdomain'>
            <Input
              invalid={errors.includes('insureio_subdomain')}
              onChange={handleInputChange.bind(null, 'insureio_subdomain')}
              readOnly={data.is_archived}
              required={true}
              value={data.insureio_subdomain}
            />
          </Field>
          <Field label='Brand ID'>
            <Input
              invalid={errors.includes('insureio_brand_id')}
              onChange={handleInputChange.bind(null, 'insureio_brand_id')}
              readOnly={data.is_archived}
              required={true}
              value={data.insureio_brand_id}
            />
          </Field>
          <Field label='Agent ID'>
            <Input
              invalid={errors.includes('insureio_agent_id')}
              onChange={handleInputChange.bind(null, 'insureio_agent_id')}
              readOnly={data.is_archived}
              required={true}
              value={data.insureio_agent_id}
            />
          </Field>
          <Field label='Lead Type'>
            <Input
              onChange={handleInputChange.bind(null, 'insureio_lead_type')}
              readOnly={data.is_archived}
              value={data.insureio_lead_type}
            />
          </Field>
        </FieldGroup>
      ) : null}

      {data.crm === 'smart_office' ? (
        <FieldGroup contained={true} label='SmartOffice Settings'>
          <Field label='Office Name'>
            <Input
              invalid={errors.includes('smart_office_office_name')}
              onChange={handleInputChange.bind(null, 'smart_office_office_name')}
              readOnly={data.is_archived}
              required={true}
              value={data.smart_office_office_name}
            />
          </Field>
          <Field label='Username'>
            <Input
              invalid={errors.includes('smart_office_username')}
              onChange={handleInputChange.bind(null, 'smart_office_username')}
              readOnly={data.is_archived}
              required={true}
              value={data.smart_office_username}
            />
          </Field>
          <Field label='Site Name'>
            <Input
              invalid={errors.includes('smart_office_site_name')}
              onChange={handleInputChange.bind(null, 'smart_office_site_name')}
              readOnly={data.is_archived}
              required={true}
              value={data.smart_office_site_name}
            />
          </Field>
          <Field label='Promo Code'>
            <Input
              invalid={errors.includes('smart_office_promo_code')}
              onChange={handleInputChange.bind(null, 'smart_office_promo_code')}
              readOnly={data.is_archived}
              required={true}
              value={data.smart_office_promo_code}
            />
          </Field>
        </FieldGroup>
      ) : null}

      {['ixn', 'insureio'].includes(data.crm) ? (
        <>
          <Field label='Lead Form Button Label'>
            <Input
              onChange={handleInputChange.bind(null, 'lead_form_btn_label')}
              readOnly={data.is_archived}
              value={data.lead_form_btn_label}
            />
          </Field>

          <Field label='Lead Form Success Message'>
            <Input
              height={120}
              onChange={handleInputChange.bind(null, 'lead_form_success_message')}
              readOnly={data.is_archived}
              type='textarea'
              value={data.lead_form_success_message}
            />
          </Field>
        </>
      ) : null}

      <div style={{ display: 'flex', marginTop: 30 }}>
        <Button color='RED' onClick={handleArchiveToggle} style={{ marginRight: '5px' }}>
          {data.is_archived ? 'Unarchive' : 'Archive'}
        </Button>
        {data.is_archived ? null : <Button onClick={handleSave}>Save</Button>}
      </div>
    </div>
  );
};

General.propTypes = {
  updateWebsiteQuoter: PropTypes.func,
  website_quoter: PropTypes.object
};

export default General;
